/**
 * Check for a new version now, and take it if there is one (bugs.md: "we need a way to force an
 * update"): the header's version asks. Unlike the notice (app/PwaBridge.tsx) this doesn't wait for the
 * member to choose, since asking is choosing, and it ignores the quiet spell after an Update. A new
 * version that's found is installed, takes over, and the page reloads onto it; with none, it says so.
 * Loaded on the tap, so it costs the app shell nothing.
 */
import { leaveForUpdate } from './leaving.js'

export type UpdateResult = 'updating' | 'latest' | 'offline'

/** How long a found version may take to install before this gives up and reloads anyway. */
const INSTALL_WAIT_MS = 30_000

export async function checkForUpdate(): Promise<UpdateResult> {
  if (!navigator.onLine) return 'offline'
  const reg = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined
  // No service worker (a private window, a browser without them): a reload is the newest there is.
  if (!reg) {
    leaveForUpdate()
    window.location.reload()
    return 'updating'
  }
  await reg.update().catch(() => {})
  const waiting = reg.waiting ?? (reg.installing ? await installed(reg.installing) : null)
  if (!waiting) return 'latest'
  // Unsent words don't stop it (lib/leaving.ts): the guards stand aside, and the chat keeps them.
  leaveForUpdate()
  // Reload once it has taken over; if that never comes, reload anyway, so the tap never sits there.
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true })
  waiting.postMessage({ type: 'SKIP_WAITING' })
  setTimeout(() => window.location.reload(), 8000)
  return 'updating'
}

/** The worker once it has installed (and waits), or null if it fails or takes too long. */
function installed(w: ServiceWorker): Promise<ServiceWorker | null> {
  return new Promise((resolve) => {
    const done = (v: ServiceWorker | null) => {
      clearTimeout(timer)
      w.removeEventListener('statechange', change)
      resolve(v)
    }
    const change = () => {
      if (w.state === 'installed') done(w)
      else if (w.state === 'redundant') done(null)
    }
    const timer = setTimeout(() => done(null), INSTALL_WAIT_MS)
    w.addEventListener('statechange', change)
  })
}
